import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Dialog, InputBase, Typography } from '@mui/material';
import {
  AdminPanelSettingsOutlined,
  ApartmentOutlined,
  ArticleOutlined,
  Brightness4Outlined,
  CenterFocusStrongOutlined,
  CreateOutlined,
  DashboardCustomizeOutlined,
  DescriptionOutlined,
  FeedbackOutlined,
  FolderOutlined,
  HomeOutlined,
  InsightsOutlined,
  KeyboardOutlined,
  MicNoneOutlined,
  NewReleasesOutlined,
  PersonOutline,
  Search,
  SettingsOutlined,
  ViewSidebarOutlined,
  WebAssetOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/EnterpriseAuthContext';
import { useWorkspaceLayout } from '../../context/WorkspaceLayoutContext';
import { InkyMascot } from '../brand';
import { formatShortcut } from '../../hooks/useWorkspaceShortcuts';
import {
  customerPath,
  documentEditorPath,
  goToRecordMeeting,
  projectPath,
  readRecentDocuments,
  RECENT_DOCUMENTS_EVENT,
  requestQuickCreate,
  sessionPath,
  templateEditorPath,
} from '../../utils/navigation';
import { Kbd } from './ShortcutsHelpDialog';
import { WHATS_NEW_PATH } from '../WhatsNewPage';
import { CONSOLE_PATH, usePlatformAdminAccess } from '../console/platformAdminApi';

const PER_GROUP_LIMIT = 5;

/**
 * Lowercase, accent-insensitive text for matching.
 * @param {string} text - Text.
 * @returns {string} Normalized text.
 */
const normalize = (text) => String(text || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '');

/**
 * Match score of a query against an item (0 = no match). Every query word must appear.
 * @param {string} query - Normalized query.
 * @param {{label: string, keywords?: string}} item - Item.
 * @returns {number} Score (higher is better).
 */
export function scoreItem(query, item) {
  if (!query) return 1;
  const label = normalize(item.label);
  const haystack = `${label} ${normalize(item.keywords)}`;
  const words = query.split(/\s+/).filter(Boolean);
  if (!words.every((word) => haystack.includes(word))) return 0;
  if (label.startsWith(query)) return 3;
  if (label.includes(query)) return 2;
  return 1;
}

/**
 * The open palette. Mounted only while open, so the workspace data it searches is not
 * subscribed to (and re-rendered) the rest of the time.
 * @param {object} props - Same as CommandPalette.
 * @returns {JSX.Element} Dialog.
 */
function CommandPaletteDialog({ isDarkMode, toggleTheme, onOpenFeedback }) {
  const navigate = useNavigate();
  const { state: appState } = useApp();
  const { actions: authActions } = useAuth();
  const layout = useWorkspaceLayout();
  const { setCommandPaletteOpen } = layout;
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [recent, setRecent] = useState(readRecentDocuments);
  const listRef = useRef(null);
  const close = () => setCommandPaletteOpen(false);

  useEffect(() => {
    const onRecent = () => setRecent(readRecentDocuments());
    window.addEventListener(RECENT_DOCUMENTS_EVENT, onRecent);
    return () => window.removeEventListener(RECENT_DOCUMENTS_EVENT, onRecent);
  }, []);

  const canManageTemplates = authActions.canManageTemplates();
  const isAdmin = authActions.isAccountAdmin();
  const isPlatformAdmin = usePlatformAdminAccess();

  /** Static commands (navigation, Inky, workspace). */
  const commands = useMemo(() => {
    const go = (path) => () => navigate(path);
    const list = [
      { id: 'nav-home', group: 'Go to', label: 'Home', icon: <HomeOutlined />, perform: go('/home') },
      { id: 'nav-recordings', group: 'Go to', label: 'Recordings', keywords: 'sessions meetings transcripts', icon: <MicNoneOutlined />, perform: go('/customers/sessions') },
      { id: 'nav-customers', group: 'Go to', label: 'Customers', keywords: 'clients accounts', icon: <ApartmentOutlined />, perform: go('/customers') },
      { id: 'nav-projects', group: 'Go to', label: 'Projects', icon: <FolderOutlined />, perform: go('/projects') },
      { id: 'nav-templates', group: 'Go to', label: 'Templates', icon: <DashboardCustomizeOutlined />, perform: go('/templates') },
      { id: 'nav-documents', group: 'Go to', label: 'Documents', icon: <DescriptionOutlined />, perform: go('/documents') },
      { id: 'act-generate', group: 'Create', label: 'Generate a document', keywords: 'new document create write', icon: <CreateOutlined />, perform: go('/generate') },
      { id: 'act-record', group: 'Create', label: 'Record a meeting', keywords: 'new recording bot meet zoom teams', icon: <MicNoneOutlined />, perform: () => goToRecordMeeting(navigate) },
      { id: 'act-new-project', group: 'Create', label: 'New project', keywords: 'create project', icon: <FolderOutlined />, perform: () => requestQuickCreate('project') },
      { id: 'act-new-customer', group: 'Create', label: 'New customer', keywords: 'create customer client account', icon: <ApartmentOutlined />, perform: () => requestQuickCreate('customer') },
      { id: 'nav-profile', group: 'Go to', label: 'Profile', keywords: 'account me', icon: <PersonOutline />, perform: go('/profile') },
      {
        id: 'act-settings',
        group: 'Workspace',
        label: 'Settings',
        keywords: 'preferences billing plan',
        icon: <SettingsOutlined />,
        perform: () => window.dispatchEvent(new Event('quillai:open-settings')),
      },
      { id: 'inky-toggle', group: 'Inky', label: layout.dock.open ? 'Hide Inky' : 'Show Inky', keywords: 'assistant chat ai', shortcut: 'Mod+J', icon: <InkyMascot size={20} decorative animated={false} />, perform: () => (layout.dock.open ? layout.toggleDock(false) : layout.openInky({ focus: true })) },
      { id: 'inky-move', group: 'Inky', label: 'Move Inky to the next position', keywords: 'dock side left right bottom float', shortcut: 'Mod+\\', icon: <ViewSidebarOutlined />, perform: () => layout.cycleDockSide() },
      { id: 'inky-float', group: 'Inky', label: 'Float Inky in a window', keywords: 'dock floating detach', icon: <WebAssetOutlined />, perform: () => layout.setDock({ side: 'floating', open: true }) },
      { id: 'ws-focus', group: 'Workspace', label: layout.focusMode ? 'Leave focus mode' : 'Focus mode', keywords: 'distraction free zen fullscreen', shortcut: 'Mod+Shift+F', icon: <CenterFocusStrongOutlined />, perform: () => layout.toggleFocusMode() },
      { id: 'ws-sidebar', group: 'Workspace', label: layout.sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar', keywords: 'navigation rail', icon: <ViewSidebarOutlined />, perform: () => layout.toggleSidebar() },
      { id: 'ws-theme', group: 'Workspace', label: isDarkMode ? 'Switch to light theme' : 'Switch to dark theme', keywords: 'dark mode light mode appearance', icon: <Brightness4Outlined />, perform: toggleTheme },
      {
        id: 'ws-whats-new',
        group: 'Workspace',
        label: "What's new",
        keywords: 'release notes changelog updates news',
        icon: <NewReleasesOutlined />,
        perform: () => window.open(WHATS_NEW_PATH, '_blank', 'noopener'),
      },
      { id: 'ws-shortcuts', group: 'Workspace', label: 'Keyboard shortcuts', keywords: 'keys hotkeys help', shortcut: '?', icon: <KeyboardOutlined />, perform: () => layout.setShortcutsHelpOpen(true) },
    ];
    if (canManageTemplates) {
      // Goes through the plan's template limit check, like the Templates page.
      list.push({ id: 'act-new-template', group: 'Create', label: 'New template', keywords: 'create template', icon: <DashboardCustomizeOutlined />, perform: () => requestQuickCreate('template') });
    }
    if (isAdmin) list.push({ id: 'nav-admin', group: 'Go to', label: 'Admin', keywords: 'members users', icon: <AdminPanelSettingsOutlined />, perform: go('/admin') });
    if (isPlatformAdmin) list.push({ id: 'nav-console', group: 'Go to', label: 'Platform console', keywords: 'tenants tiers isolation owner', icon: <InsightsOutlined />, perform: go(CONSOLE_PATH) });
    if (onOpenFeedback) list.push({ id: 'ws-feedback', group: 'Workspace', label: 'Send feedback', icon: <FeedbackOutlined />, perform: onOpenFeedback });
    return list;
  }, [navigate, layout, isDarkMode, toggleTheme, canManageTemplates, isAdmin, isPlatformAdmin, onOpenFeedback]);

  /** Searchable entities from the loaded workspace data. */
  const entities = useMemo(() => {
    const projects = appState.projects || [];
    const documents = [];
    const sessions = [];
    projects.forEach((project) => {
      (project.generatedDocuments || []).forEach((doc) => documents.push({ doc, project }));
      (project.sessions || []).forEach((session) => sessions.push({ session, project }));
    });
    documents.sort((a, b) => String(b.doc.createdAt || '').localeCompare(String(a.doc.createdAt || '')));
    return { projects, documents, sessions, customers: appState.customers || [], templates: appState.templates || [] };
  }, [appState.projects, appState.customers, appState.templates]);

  const sections = useMemo(() => {
    const q = normalize(query.trim());
    const out = [];
    const push = (group, items) => {
      if (items.length) out.push({ group, items });
    };

    if (q) {
      push('Inky', [{
        id: 'inky-ask',
        label: `Ask Inky: ${query.trim()}`,
        icon: <InkyMascot size={20} decorative animated={false} />,
        perform: () => layout.openInky({ prompt: query.trim() }),
      }]);
    }

    const recentItems = recent.map((entry) => {
      const found = entities.documents.find((d) => d.doc.id === entry.id);
      return {
        id: `recent-${entry.id}`,
        label: found?.doc.name || entry.name,
        hint: found?.project?.name || 'Recently opened',
        keywords: found?.project?.name,
        icon: <ArticleOutlined />,
        perform: () => navigate(documentEditorPath(entry.id, { projectId: found?.project?.id || entry.projectId })),
      };
    });
    if (!q) push('Recent documents', recentItems.slice(0, PER_GROUP_LIMIT));

    const ranked = (items) => items
      .map((item) => ({ item, score: scoreItem(q, item) }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.item);

    if (q) {
      const recentIds = new Set(recent.map((r) => r.id));
      const docItems = [
        ...recentItems,
        ...entities.documents
          .filter(({ doc }) => !recentIds.has(doc.id))
          .map(({ doc, project }) => ({
            id: `doc-${doc.id}`,
            label: doc.name || 'Untitled document',
            hint: project.name,
            keywords: `${project.name} ${doc.templateName || ''}`,
            icon: <ArticleOutlined />,
            perform: () => navigate(documentEditorPath(doc.id, { projectId: project.id })),
          })),
      ];
      push('Documents', ranked(docItems).slice(0, PER_GROUP_LIMIT));
      push('Projects', ranked(entities.projects.map((project) => ({
        id: `project-${project.id}`,
        label: project.name,
        hint: project.customer?.name,
        keywords: project.customer?.name,
        icon: <FolderOutlined />,
        perform: () => navigate(projectPath(project.id)),
      }))).slice(0, PER_GROUP_LIMIT));
      push('Customers', ranked(entities.customers.map((customer) => ({
        id: `customer-${customer.id}`,
        label: customer.name,
        icon: <ApartmentOutlined />,
        perform: () => navigate(customerPath(customer.id)),
      }))).slice(0, PER_GROUP_LIMIT));
      push('Recordings', ranked(entities.sessions.map(({ session, project }) => ({
        id: `session-${session.id}`,
        label: session.name || 'Untitled recording',
        hint: project.name,
        keywords: project.name,
        icon: <MicNoneOutlined />,
        perform: () => navigate(sessionPath(session.id)),
      }))).slice(0, PER_GROUP_LIMIT));
      if (canManageTemplates) {
        push('Templates', ranked(entities.templates.map((template) => ({
          id: `template-${template.id}`,
          label: template.name,
          icon: <DashboardCustomizeOutlined />,
          perform: () => navigate(templateEditorPath({ templateId: template.id, kind: template.templateType })),
        }))).slice(0, PER_GROUP_LIMIT));
      }
    }

    const commandGroups = ['Go to', 'Create', 'Inky', 'Workspace'];
    commandGroups.forEach((group) => {
      const items = ranked(commands.filter((c) => c.group === group));
      const existing = out.find((s) => s.group === group);
      if (existing) existing.items.push(...items);
      else push(group, items);
    });
    return out;
  }, [query, commands, entities, recent, navigate, layout, canManageTemplates]);

  const flat = useMemo(() => sections.flatMap((section) => section.items), [sections]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  const run = (item) => {
    if (!item) return;
    close();
    // Let the dialog close (and return focus) before the command moves focus elsewhere.
    requestAnimationFrame(() => item.perform());
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => (flat.length ? (i + 1) % flat.length : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => (flat.length ? (i - 1 + flat.length) % flat.length : 0));
    } else if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault();
      run(flat[activeIndex]);
    } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      close();
    }
  };

  let index = -1;
  const listId = 'q-command-list';
  return (
    <Dialog
      open
      onClose={close}
      fullWidth
      maxWidth="sm"
      aria-label="Command palette"
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      PaperProps={{ sx: { mt: { xs: 2, sm: '12vh' }, borderRadius: 'var(--q-radius-xl, 12px)', overflow: 'hidden', maxHeight: { xs: 'calc(100% - 32px)', sm: '70vh' } } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, height: 52, borderBottom: '1px solid var(--q-border-subtle, #ECE9E4)' }}>
        <Search sx={{ color: 'var(--q-muted, #5E5A54)', fontSize: 20 }} />
        <InputBase
          autoFocus
          fullWidth
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search or ask Inky"
          inputProps={{
            role: 'combobox',
            'aria-expanded': 'true',
            'aria-controls': listId,
            'aria-autocomplete': 'list',
            'aria-activedescendant': flat[activeIndex] ? `q-cmd-${flat[activeIndex].id}` : undefined,
            'aria-label': 'Search or ask Inky',
          }}
          sx={{ fontSize: 15 }}
        />
        <Kbd>Esc</Kbd>
      </Box>
      <Box ref={listRef} id={listId} role="listbox" aria-label="Results" sx={{ overflowY: 'auto', py: 0.75 }}>
        {flat.length === 0 && (
          <Typography variant="body2" sx={{ px: 2, py: 3, textAlign: 'center', color: 'var(--q-muted, #5E5A54)' }}>
            Nothing matches. Press Enter to ask Inky.
          </Typography>
        )}
        {sections.map((section) => (
          <Box key={section.group} role="group" aria-label={section.group} sx={{ mb: 0.5 }}>
            <Typography variant="caption" component="div" sx={{ px: 2, pt: 1, pb: 0.5, fontWeight: 600, color: 'var(--q-faint, #716C65)', letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: 11 }}>
              {section.group}
            </Typography>
            {section.items.map((item) => {
              index += 1;
              const itemIndex = index;
              const active = itemIndex === activeIndex;
              return (
                <Box
                  key={item.id}
                  id={`q-cmd-${item.id}`}
                  role="option"
                  aria-selected={active}
                  data-index={itemIndex}
                  onMouseMove={() => !active && setActiveIndex(itemIndex)}
                  onClick={() => run(item)}
                  sx={{
                    mx: 1,
                    px: 1.25,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    borderRadius: 'var(--q-radius-md, 8px)',
                    cursor: 'pointer',
                    color: 'var(--q-text, #1C1B19)',
                    bgcolor: active ? 'var(--q-accent-selected, #E7E3FA)' : 'transparent',
                    '& .q-cmd-icon': { display: 'flex', color: active ? 'var(--q-accent-strong, #2F2096)' : 'var(--q-muted, #5E5A54)', '& svg': { fontSize: 20 } },
                  }}
                >
                  <span className="q-cmd-icon">{item.icon}</span>
                  <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: active ? 600 : 500 }}>
                    {item.label}
                  </Typography>
                  {item.hint && (
                    <Typography noWrap variant="caption" sx={{ color: 'var(--q-muted, #5E5A54)', maxWidth: '40%' }}>
                      {item.hint}
                    </Typography>
                  )}
                  {item.shortcut && <Kbd>{formatShortcut(item.shortcut)}</Kbd>}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Dialog>
  );
}

/**
 * Command palette (Mod+K): go anywhere, find documents/projects/customers/templates/recordings,
 * ask Inky, and run workspace commands.
 * @param {object} props - Props.
 * @param {boolean} props.isDarkMode - Current theme.
 * @param {() => void} props.toggleTheme - Theme toggle.
 * @param {() => void} [props.onOpenFeedback] - Open the feedback dialog.
 * @returns {JSX.Element|null} Palette dialog while open.
 */
export default function CommandPalette(props) {
  const { commandPaletteOpen } = useWorkspaceLayout();
  return commandPaletteOpen ? <CommandPaletteDialog {...props} /> : null;
}
